import { useMutation, useQuery, useQueryClient } from 'react-query';
import { apiGet, apiUpload, apiDelete } from '../services/apiClient';
import { API_URL } from '../Constants';

/**
 * The icon library — icons an operator uploads from the CMS and reuses across
 * the website, as opposed to `config/websiteIcons.json`, which is a generated
 * inventory of files the WEBSITE already ships and that no operator can add to.
 *
 * Backend routes (Swagger, /api/Icons):
 *
 *   GET    /api/Icons?ModuleKey=&SubModuleKey=&SearchText=&IncludeGeneral=&IncludeInactive=
 *   POST   /api/Icons          multipart: Name, ModuleKey, SubModuleKey, File, IsActive
 *   DELETE /api/Icons/{id}
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ THIS CONTROLLER DOES NOT PAGINATE.                                       │
 * │                                                                          │
 * │ The list route is a BARE path with filters in the query string — there    │
 * │ is no `/{page}/{pageSize}`, so this uses `apiGet` and NOT `apiGetList`.   │
 * │ `apiGetList('Icons')` would request `/api/Icons/1/0` and 404. The reply   │
 * │ is the enveloped `{ …, data: [ … ] }` with `data` a plain ARRAY, not the  │
 * │ `{ items, totalCount }` page object contract §5.1 describes.             │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * QUERY KEYS ARE PascalCase HERE, DELIBERATELY. Contract §8 says camelCase and
 * every other call in this repo obeys it; Swagger declares these five as
 * `ModuleKey`, `SubModuleKey`, `SearchText`, `IncludeGeneral`, `IncludeInactive`.
 * ASP.NET binds query strings case-insensitively so either spelling works today,
 * but the Swagger spelling is the documented one and is what a backend engineer
 * will search the controller for. Same for the multipart field names on POST,
 * where the binding is NOT forgiving in every configuration.
 */

const RESOURCE = 'Icons';

export const iconKeys = {
  all: ['icons'],
  list: (filters = {}) => ['icons', 'list', filters],
};

/* -------------------------------------------------------------------------- */
/*  Reading                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The uploaded icons for one module.
 *
 * `moduleKey` is REQUIRED by the backend's own filter semantics — omitting it
 * returns every module's icons mixed together, which is never what a picker
 * wants — so the query stays disabled until one is supplied rather than firing
 * a request whose result would be wrong.
 *
 * `includeGeneral` asks the backend to add the module-less icons (the shared
 * pool) to the module's own. On by default: an operator opening a picker wants
 * everything they are allowed to choose from, not a subset defined by which
 * screen happened to upload the file.
 *
 * `includeInactive` is OFF by default: a deactivated icon is one someone has
 * already decided not to use, and offering it again is noise.
 *
 * @returns {{ data: Array<{ id, name, moduleKey, subModuleKey, blobUrl,
 *             imageUrl, format, isActive }> }}
 */
export const useIcons = (
  moduleKey,
  {
    subModuleKey,
    searchText,
    includeGeneral = true,
    includeInactive = false,
    enabled = true,
  } = {}
) => {
  const filters = {
    ModuleKey: moduleKey,
    SubModuleKey: subModuleKey,
    SearchText: searchText,
    IncludeGeneral: includeGeneral,
    IncludeInactive: includeInactive,
  };

  return useQuery({
    queryKey: iconKeys.list(filters),
    queryFn: async () => {
      const data = await apiGet(RESOURCE, filters);
      // `data` is documented as an array; a backend that one day wraps it in a
      // page object should not blank the gallery with no explanation.
      return Array.isArray(data) ? data : (data?.items ?? []);
    },
    enabled: Boolean(moduleKey) && enabled,
    refetchOnWindowFocus: false,
  });
};

/* -------------------------------------------------------------------------- */
/*  Writing                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Upload one icon into the library.
 *
 * `name` and `moduleKey` are mandatory and `file` is the whole point, so all
 * three are asserted here rather than left to a 400 — the backend's per-field
 * validation map is useful, but an empty Name is a UI bug this catches at the
 * call site.
 *
 * `isActive` defaults to TRUE: an operator uploads an icon in order to use it,
 * and an upload that silently landed deactivated would look like a failure.
 *
 * `SubModuleKey` is optional and is omitted entirely when blank — sending an
 * empty string would file the icon under a sub-module literally named "", which
 * no later `?SubModuleKey=` filter could ever match again.
 *
 * @param {{ name: string, moduleKey: string, subModuleKey?: string,
 *           file: File, isActive?: boolean }} input
 */
export const useCreateIcon = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ name, moduleKey, subModuleKey, file, isActive = true }) => {
      const trimmedName = (name ?? '').trim();
      if (!trimmedName) throw new Error('اسم الأيقونة مطلوب.');
      if (!moduleKey) throw new Error('مفتاح الوحدة (ModuleKey) مطلوب.');
      if (!file) throw new Error('يجب اختيار ملف الأيقونة.');

      const formData = new FormData();
      formData.append('Name', trimmedName);
      formData.append('ModuleKey', moduleKey);
      if (subModuleKey) formData.append('SubModuleKey', subModuleKey);
      formData.append('File', file);
      formData.append('IsActive', String(isActive));

      return apiUpload(RESOURCE, formData);
    },
    { onSuccess: () => queryClient.invalidateQueries(iconKeys.all) }
  );
};

/** Remove an icon from the library. Swagger takes the uuid as a path segment. */
export const useDeleteIcon = () => {
  const queryClient = useQueryClient();
  return useMutation((id) => apiDelete(RESOURCE, id), {
    onSuccess: () => queryClient.invalidateQueries(iconKeys.all),
  });
};

/* -------------------------------------------------------------------------- */
/*  Shaping                                                                    */
/* -------------------------------------------------------------------------- */

const isAbsolute = (value) =>
  /^(https?:)?\/\//i.test(value) || /^data:/i.test(value);

/**
 * The address to STORE for an uploaded icon — and therefore the one the public
 * website will resolve.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ `blobUrl` IS A CONTAINER AND `imageUrl` IS A BARE FILE NAME. JOIN THEM.   │
 * │                                                                          │
 * │ Measured 2026-10-05, `GET /api/Icons?ModuleKey=NavigationMenu`:           │
 * │                                                                          │
 * │   "blobUrl":  "https://dpablobstoragedev.blob.core.windows.net/dpa-development"
 * │   "imageUrl": "cf39df6e-a9a6-45ff-a578-9548142e85f6.svg"                  │
 * │                                                                          │
 * │ NEITHER FIELD IS USABLE ALONE. Despite its name `imageUrl` is not a URL   │
 * │ and not even a path — it is the blob's name — and `blobUrl`, despite its  │
 * │ name, addresses the DIRECTORY. Taking `imageUrl` as the address (the      │
 * │ obvious reading, and the first thing this function did) produced          │
 * │ `https://dpaapis.ctsdemo.ae/cf39df6e-….svg`, a 404 on the wrong host, and │
 * │ every library tile rendered «غير موجود».                                 │
 * │                                                                          │
 * │ The same trap is already documented on `extractUploadedUrl` in            │
 * │ `Hooks/NavigationMenu` — this API has answered in the split shape before. │
 * │ When a field called `…Url` turns out not to be one, join, do not guess.   │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * The result is ABSOLUTE, and deliberately so. An icon in `websiteIcons.json`
 * is a file the WEBSITE serves, so a root-relative path is stored and the
 * website resolves it against itself; an uploaded icon lives in a blob store on
 * another origin entirely, so a relative value would send the website looking
 * in its own `public/` folder for a file that is not there. Absolutising here is
 * what keeps the two kinds of icon interchangeable everywhere downstream.
 */
export const iconLibraryUrl = (icon) => {
  const container = (icon?.blobUrl ?? '').trim().replace(/\/+$/, '');
  const name = (icon?.imageUrl ?? '').trim().replace(/^\/+/, '');

  // An `imageUrl` that IS a full URL — a future backend that stops splitting,
  // or a general icon pointing at something already hosted. It wins outright.
  if (name && isAbsolute(name)) return name;

  // The measured shape.
  if (container && name) return `${container}/${name}`;

  // A name with no container: the API origin is the only sensible base left.
  if (name) return `${API_URL}/${name}`;

  /**
   * A container with no file name addresses a DIRECTORY, so there is nothing
   * to draw and nothing worth storing — unless it carries a file extension, in
   * which case it is a whole address after all.
   *
   * Returning '' rather than the directory is what lets `IconPicker` drop the
   * row instead of painting a tile that can never load. A row reaching here is
   * a backend bug (an icon with no blob), and a gap in the gallery is the
   * honest rendering of one.
   */
  if (container && /\.[a-z0-9]{2,5}$/i.test(container)) return container;

  return '';
};
